import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { tokenService, type ApiTokenInfo } from '@/services/tokenService';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Copy, Key, Trash2 } from 'lucide-react';
import { TableSkeleton } from '@/components/ui/page-skeleton';

export default function ApiTokensSection() {
  const [tokens, setTokens] = useState<ApiTokenInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);

  // Created-token reveal dialog
  const [revealedToken, setRevealedToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Revoke confirm dialog
  const [revokeTarget, setRevokeTarget] = useState<ApiTokenInfo | null>(null);

  const load = async () => {
    try {
      const res = await tokenService.list();
      setTokens(res.tokens);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Failed to load tokens';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setCreating(true);
    try {
      const res = await tokenService.create(trimmed);
      setRevealedToken(res.token);
      setCopied(false);
      setName('');
      await load();
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Failed to create token';
      toast.error(message);
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id: string) => {
    try {
      await tokenService.revoke(id);
      setRevokeTarget(null);
      toast.success('Token revoked');
      await load();
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Failed to revoke token';
      toast.error(message);
    }
  };

  const copyToken = async () => {
    if (!revealedToken) return;
    await navigator.clipboard.writeText(revealedToken);
    setCopied(true);
    toast.success('Copied to clipboard');
  };

  const activeTokens = tokens.filter(t => !t.revoked);
  const revokedTokens = tokens.filter(t => t.revoked);

  const formatDate = (iso: string | null) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            API Tokens
          </CardTitle>
          <CardDescription>
            Generate bearer tokens for programmatic access (MCP, scripts, CI/CD)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Create form */}
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Token name, e.g. My MCP agent"
              className="flex-1"
              onKeyDown={e => e.key === 'Enter' && create()}
            />
            <Button onClick={create} disabled={!name.trim() || creating}>
              <Plus className="mr-2 h-4 w-4" />
              Generate Token
            </Button>
          </div>

          {/* Token list */}
          {loading ? (
            <TableSkeleton rows={3} />
          ) : (
            <div className="rounded-md border">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b bg-muted/50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium">Name</th>
                      <th className="px-4 py-3 text-left font-medium">Prefix</th>
                      <th className="px-4 py-3 text-left font-medium">Created</th>
                      <th className="px-4 py-3 text-left font-medium">Last Used</th>
                      <th className="px-4 py-3 text-right font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {activeTokens.length === 0 && revokedTokens.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                          No API tokens yet
                        </td>
                      </tr>
                    ) : (
                      <>
                        {activeTokens.map(t => (
                          <tr key={t.id} className="hover:bg-muted/50">
                            <td className="px-4 py-3 font-medium">{t.name}</td>
                            <td className="px-4 py-3">
                              <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                                {t.prefix}...
                              </code>
                            </td>
                            <td className="px-4 py-3 text-muted-foreground">{formatDate(t.created_at)}</td>
                            <td className="px-4 py-3 text-muted-foreground">{formatDate(t.last_used_at)}</td>
                            <td className="px-4 py-3 text-right">
                              <Button
                                onClick={() => setRevokeTarget(t)}
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                        {revokedTokens.map(t => (
                          <tr key={t.id} className="hover:bg-muted/50 opacity-50">
                            <td className="px-4 py-3 font-medium line-through">{t.name}</td>
                            <td className="px-4 py-3">
                              <code className="rounded bg-muted px-1.5 py-0.5 text-xs line-through">
                                {t.prefix}...
                              </code>
                            </td>
                            <td className="px-4 py-3 text-muted-foreground">{formatDate(t.created_at)}</td>
                            <td className="px-4 py-3 text-muted-foreground">{formatDate(t.last_used_at)}</td>
                            <td className="px-4 py-3 text-right text-xs text-muted-foreground">Revoked</td>
                          </tr>
                        ))}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Token reveal dialog */}
      <Dialog open={!!revealedToken} onOpenChange={open => { if (!open) setRevealedToken(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Token Created</DialogTitle>
            <DialogDescription>
              Copy your token now. It will not be shown again.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border bg-muted p-3 font-mono text-xs break-all select-all">
            {revealedToken}
          </div>
          <DialogFooter>
            <Button onClick={copyToken} variant={copied ? 'secondary' : 'default'}>
              <Copy className="mr-2 h-4 w-4" />
              {copied ? 'Copied!' : 'Copy Token'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke confirm */}
      <AlertDialog open={!!revokeTarget} onOpenChange={open => { if (!open) setRevokeTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke token?</AlertDialogTitle>
            <AlertDialogDescription>
              Token <strong>{revokeTarget?.name}</strong> ({revokeTarget?.prefix}...) will be permanently revoked. Any integrations using it will stop working.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => revokeTarget && revoke(revokeTarget.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
